import { Button } from '@startuptools/ui/Button';
import { Stack } from '@startuptools/ui/Stack';
import { Input } from '@startuptools/ui/Input';
import { Box } from '@startuptools/ui/Box';
import { IconButton } from '@startuptools/ui/IconButton';
import { ButtonGroup } from '@startuptools/ui/ButtonGroup';
import { Typography } from '@startuptools/ui/Typography';
import { RadioGroup } from '@startuptools/ui/RadioGroup';
import { Radio } from '@startuptools/ui/Radio';
import { CircularProgress } from '@startuptools/ui/CircularProgress';
import { Card } from '@startuptools/ui/Card';
import { Alert } from '@startuptools/ui/Alert';
import { DateFormat } from '@startuptools/ui/DateFormat';
import { GenericError } from '@startuptools/ui/Error';
import { NumberFormat } from '@startuptools/ui/NumberFormat';
import { Table } from '@startuptools/ui/Table';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import { NumericFormatInput } from '@startuptools/ui/NumericFormatInput';
import React from 'react';
import { Trans } from '@lingui/react';
import { DateInput } from '@startuptools/ui/DateInput';
import { useLingui, I18nProvider } from '@lingui/react';
import { I18n } from '@lingui/core';
import { DateTime } from 'luxon';
import {
  useCreateCustomVestingMutation,
  useDeleteCustomVestingMutation,
  useUpdateCustomVestingMutation,
} from './custom-vesting.graphql';
import { type DocumentNode } from 'graphql';
import { useForm, Controller } from 'react-hook-form';
import {
  type GqlWarrantParticipant,
  type GqlWarrantParticipantVestingOccasion,
  type GqlWarrantProgram,
} from '../../../../../../../graphql/base-types.graphql';
import { BigNumber } from 'bignumber.js';

export type DisplayVestingOccasion = Omit<
  GqlWarrantParticipantVestingOccasion,
  'warrantParticipantId' | 'warrantParticipant'
>;

type FormData = {
  vestingDate: DateTime | null;
  quantity: string;
  requirementPrimaryLang: string;
  requirementSecondaryLang: string;
};

const CustomVestingRow = ({
  wp,
  wpp,
  vestingOccasion,
  refetchQueries,
}: {
  wp: Pick<GqlWarrantProgram, 'warrantStartDate' | 'shareSubscribeStartDate'>;
  wpp: Pick<GqlWarrantParticipant, 'quantity'>;
  vestingOccasion: DisplayVestingOccasion;
  refetchQueries: (string | DocumentNode)[];
}) => {
  const { i18n } = useLingui();
  const { register, handleSubmit, control } = useForm<FormData>({
    mode: 'onBlur',
    defaultValues: {
      vestingDate: vestingOccasion.vestingDate ? DateTime.fromISO(vestingOccasion.vestingDate) : null,
      quantity: vestingOccasion.quantity ?? '',
      requirementPrimaryLang: vestingOccasion.requirementPrimaryLang ?? '',
      requirementSecondaryLang: vestingOccasion.requirementSecondaryLang ?? '',
    },
  });

  const [isSaving, setIsSaving] = React.useState(false);
  // eslint-disable-next-line sut/loading-mutation
  const [deleteVestingOccasion] = useDeleteCustomVestingMutation({ refetchQueries });
  // eslint-disable-next-line sut/loading-mutation
  const [updateVestingOccasion] = useUpdateCustomVestingMutation({ refetchQueries });

  const save = async ({ vestingDate, quantity, requirementPrimaryLang, requirementSecondaryLang }: FormData) => {
    setIsSaving(true);
    await updateVestingOccasion({
      variables: {
        vestingOccasion: {
          id: vestingOccasion.id,
          vestingDate: vestingDate?.toISO(),
          quantity,
          requirementPrimaryLang,
          requirementSecondaryLang,
        },
      },
    });
    setIsSaving(false);
  };
  const formId = `vesting_form_${vestingOccasion.id}`;

  return (
    <>
      <form
        id={formId}
        onSubmit={handleSubmit(async data => {
          await save(data);
        })}
      />

      <tr>
        <td>
          <Controller
            control={control}
            name="vestingDate"
            render={({ field }) => (
              <DateInput
                {...field}
                sx={{ minWidth: 190 }}
                minDate={DateTime.fromISO(wp.warrantStartDate!)}
                maxDate={DateTime.fromISO(wp.shareSubscribeStartDate!)}
                value={field.value}
                onChange={newValue => {
                  field.onChange(newValue);
                  handleSubmit(save);
                }}
              />
            )}
          />
        </td>
        <td>
          <Controller
            control={control}
            name="quantity"
            rules={{ required: true, min: 0, max: parseInt(wpp.quantity ?? '0') }}
            render={({ field, formState }) => (
              <NumericFormatInput
                {...field}
                lang={i18n.locale}
                color={formState.errors.quantity ? 'danger' : 'neutral'}
                onBlur={handleSubmit(save)}
              />
            )}
          />
        </td>
        <td>
          <Input
            {...register('requirementPrimaryLang')}
            placeholder={i18n._('Requirement (Swedish)')}
            onBlur={handleSubmit(save)}
          />
        </td>
        <td>
          <Input
            {...register('requirementSecondaryLang')}
            placeholder={i18n._('Requirement (English)')}
            onBlur={handleSubmit(save)}
          />
        </td>
        <td>
          <ButtonGroup>
            <IconButton color="success" type="submit" form={formId}>
              {isSaving ? <CircularProgress /> : <SaveIcon />}
            </IconButton>
            {vestingOccasion && (
              <IconButton
                color="danger"
                onClick={() => {
                  void deleteVestingOccasion({ variables: { vestingOccasionId: vestingOccasion.id } });
                }}
              >
                <DeleteIcon />
              </IconButton>
            )}
          </ButtonGroup>
        </td>
      </tr>
    </>
  );
};

const TemplateText = ({ children }: { children: React.ReactNode }) => (
  <Typography
    color={children ? 'success' : 'warning'}
    component="span"
    fontWeight="bold"
    fontSize="1em"
    display="inline"
  >
    {children ?? <Trans id="...text missing" />}
  </Typography>
);

const CustomVestingPreview = ({
  vestingOccasions,
  vestingCustomKindPrimaryLang,
  vestingCustomKindSecondaryLang,
}: {
  vestingOccasions: DisplayVestingOccasion[];
  vestingCustomKindPrimaryLang?: string | null | undefined;
  vestingCustomKindSecondaryLang?: string | null | undefined;
}) => {
  const { i18n } = useLingui();
  return (
    <Box sx={{ fontSize: '0.7em' }}>
      <Typography>
        <Trans id="The vesting of the Warrants starts at the latest of the following dates" />
      </Typography>
      <ul>
        <li>
          <Trans id="when this Agreement is signed" />
        </li>
        <li>
          <Trans
            id="from the date when the Warrant holder {requirement}"
            values={{
              requirement: (
                <TemplateText>
                  {i18n.locale === 'sv' ? vestingCustomKindPrimaryLang : vestingCustomKindSecondaryLang}
                </TemplateText>
              ),
            }}
          />
        </li>
      </ul>
      <Typography>
        <Trans id="Vesting will happen according to the following:" />
      </Typography>
      <ul>
        {vestingOccasions
          .filter(vo => vo.quantity)
          .map(vo => (
            <li key={vo.id}>
              {vo.vestingDate && (
                <TemplateText>
                  <DateFormat locale={i18n.locale} suffix=": ">
                    {vo.vestingDate}
                  </DateFormat>
                </TemplateText>
              )}
              <Trans
                id="{quantity} options vest"
                values={{
                  quantity: (
                    <TemplateText>
                      <NumberFormat locale={i18n.locale} variant="shares">
                        {vo.quantity}
                      </NumberFormat>
                    </TemplateText>
                  ),
                }}
              />{' '}
              {vo.requirementPrimaryLang && (
                <Trans
                  id="if the following requirements are met: {requirement}"
                  values={{
                    requirement: (
                      <TemplateText>
                        {i18n.locale === 'sv' ? vo.requirementPrimaryLang : vo.requirementSecondaryLang}
                      </TemplateText>
                    ),
                  }}
                />
              )}
            </li>
          ))}
      </ul>
    </Box>
  );
};
export const CustomVestingForm = ({
  close,
  vestingOccasions,
  refetchQueries,
  wp,
  warrantParticipant: wpp,
  saveWpp,
}: {
  close: () => void;
  vestingOccasions: DisplayVestingOccasion[];
  refetchQueries: (string | DocumentNode)[];
  wp: Pick<GqlWarrantProgram, 'warrantStartDate' | 'shareSubscribeStartDate'>;
  warrantParticipant: Pick<
    GqlWarrantParticipant,
    'id' | 'vestingCustomKindPrimaryLang' | 'vestingCustomKindSecondaryLang' | 'quantity'
  >;
  saveWpp: (wpp: Partial<GqlWarrantParticipant>) => Promise<void>;
}) => {
  const { i18n } = useLingui();

  const vestingCustomKindPrimaryLang = wpp.vestingCustomKindPrimaryLang ?? 'Påbörjar sin anställning i Bolaget';
  const vestingCustomKindSecondaryLang = wpp.vestingCustomKindSecondaryLang ?? 'Starts their employment in the Company';

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<Pick<GqlWarrantParticipant, 'vestingCustomKindPrimaryLang' | 'vestingCustomKindSecondaryLang'>>({
    defaultValues: {
      vestingCustomKindPrimaryLang,
      vestingCustomKindSecondaryLang,
    },
  });

  const currentVestingCustomKindPrimaryLang = watch('vestingCustomKindPrimaryLang');
  const currentVestingCustomKindSecondaryLang = watch('vestingCustomKindSecondaryLang');

  const [error, setError] = React.useState(false);

  const locali18nRef = React.useRef(new I18n({ locale: i18n.locale, messages: i18n['_messages'] }));
  const [createVestingOccasion, { loading }] = useCreateCustomVestingMutation({ refetchQueries });
  const create = React.useCallback(async () => {
    await createVestingOccasion({
      variables: {
        vestingOccasion: {
          warrantParticipantId: wpp.id,
          descriptionPrimaryLang: '',
          descriptionSecondaryLang: '',
          quantity: '0',
          requirementPrimaryLang: '',
          requirementSecondaryLang: '',
          sortOrder: 0,
        },
      },
    });
  }, [createVestingOccasion, wpp.id]);
  React.useEffect(() => {
    if (vestingOccasions.length === 0) {
      void create();
    }
  }, [create, vestingOccasions.length]);

  const allocated = vestingOccasions.reduce((acc, curr) => acc.plus(new BigNumber(curr.quantity)), new BigNumber(0));
  const qt = new BigNumber(wpp.quantity ?? '0');

  return (
    <Stack minWidth={1000} maxWidth={1200} m={2} spacing={2}>
      <Stack direction="row" spacing={2}>
        <Button
          variant="outlined"
          startDecorator={<ArrowBackIcon />}
          onClick={async () => {
            try {
              await handleSubmit(saveWpp)();
              close();
            } catch {
              setError(true);
            }
          }}
        >
          <Trans id="Back" />
        </Button>
      </Stack>

      {error && <GenericError />}

      <Card>
        <Stack gap={2}>
          <Alert color={Object.keys(errors).length ? 'danger' : 'neutral'}>
            <Trans id="Edit the text to see how it fits into the template below" />
          </Alert>
          <Stack direction="row" gap={2}>
            <Input
              size="md"
              placeholder={i18n._('Description (Swedish)')}
              color={errors.vestingCustomKindPrimaryLang ? 'danger' : 'neutral'}
              {...register('vestingCustomKindPrimaryLang', { required: true })}
              onBlur={handleSubmit(saveWpp)}
            />
            <Input
              size="md"
              placeholder={i18n._('Description (English)')}
              color={errors.vestingCustomKindSecondaryLang ? 'danger' : 'neutral'}
              {...register('vestingCustomKindSecondaryLang', { required: true })}
              onBlur={handleSubmit(saveWpp)}
            />
          </Stack>
        </Stack>
      </Card>

      <Card>
        <Box>
          <Button onClick={create} loading={loading}>
            <Trans id="Add new vesting occasion" />
          </Button>
        </Box>
        <Alert color={allocated.isGreaterThan(qt) ? 'danger' : allocated.isEqualTo(qt) ? 'success' : 'neutral'}>
          <Trans
            id="{allocated} of {quantity} options are allocated. {subMessage}"
            values={{
              quantity: (
                <NumberFormat maximumFractionDigits={0} locale={i18n.locale}>
                  {qt}
                </NumberFormat>
              ),
              subMessage: allocated.isGreaterThan(qt) ? (
                <Trans id="Reduce the amount of options." />
              ) : allocated.isEqualTo(qt) ? (
                <Trans id="Well done!" />
              ) : (
                <Trans id="Allocate all options in the vesting occasions below." />
              ),
              allocated: (
                <NumberFormat maximumFractionDigits={0} locale={i18n.locale}>
                  {allocated}
                </NumberFormat>
              ),
            }}
          />
        </Alert>
        <Table
          sx={{
            '& tr th:last-child, & tr td:last-child': { width: '60px' },
            '& tr th:first-child, & tr td:first-child': { width: '250px' },
          }}
        >
          <thead>
            <tr>
              <th style={{ minWidth: 210 }}>
                <Trans id="Date" />
              </th>
              <th>
                <Trans id="Quantity" />
              </th>
              <th>
                <Trans id="Requirement (Swedish)" />
              </th>
              <th>
                <Trans id="Requirement (English)" />
              </th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {vestingOccasions.map(vo => (
              <CustomVestingRow key={vo.id} vestingOccasion={vo} refetchQueries={refetchQueries} wpp={wpp} wp={wp} />
            ))}
          </tbody>
        </Table>
      </Card>
      <Typography level="h4">
        <Trans id="Contract preview" />
      </Typography>
      <RadioGroup
        defaultValue={i18n.locale === 'sv' ? 'primary' : 'secondary'}
        onChange={e => {
          locali18nRef.current.activate(e.target.value === 'primary' ? 'sv' : 'en');
        }}
      >
        <Radio value="primary" label={<Trans id="Swedish" />} />
        <Radio value="secondary" label={<Trans id="English" />} />
      </RadioGroup>
      <Card>
        <I18nProvider i18n={locali18nRef.current}>
          <CustomVestingPreview
            vestingOccasions={vestingOccasions}
            vestingCustomKindPrimaryLang={currentVestingCustomKindPrimaryLang}
            vestingCustomKindSecondaryLang={currentVestingCustomKindSecondaryLang}
          />
        </I18nProvider>
      </Card>
    </Stack>
  );
};
